import React, { useCallback, useEffect, useState } from "react";
import BrandCard from "@components/card/BrandCard";
import styled from "@emotion/styled";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import useAxios from "@libs/axios/useAxios";
import { BRAND_URL } from "@libs/apiUrl";
import { LOAD_SIZE_10 } from "@libs/reactQuery/queryClient";
import useIntersection from "@hook/useIntersection";
import SearchInput from "@components/input/SearchInput";
import useFilterStepStore from "@store/CategoryFilter/useFilterStepStore";
import Image from "@components/item/Image";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "@emotion/react";
import BrandFilter from "./BrandFilter";
import { useApiRequest } from "@utils/ApiRequest";
import { enFilter, koreanFilter } from "@constants/Constants";
import BasicButton from "@components/button/BasicButton";
import NoSearch from "./NoSearch";

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
  padding: "0 2rem",
  position: "relative",
  minHeight: "100dvh",
});

const BrandList = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
  marginBottom: "6.4rem",
});

const InputWrapper = styled.div({
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: "2rem",
  paddingTop: "2rem",
});

const ButtonWrapper = styled.div({
  position: "fixed",
  bottom: "-0.4rem",
  left: "50%",
  transform: "translateX(-50%)",
  width: "90%",
  maxWidth: "56rem",
  padding: "2rem",
  zIndex: 10,
});

const BackButton = styled.button({
  display: "flex",
});

const FilterItem = styled.button<{ focus: boolean }>(({ theme, focus }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: "1.6rem",
  fontWeight: theme.fontWeight.medium,
  color: focus ? theme.color.green : theme.color.black,
  lineHeight: "1.6rem",
  cursor: "pointer",
}));

const BrandCategoryFilter = () => {
  const { axiosInstance } = useAxios();
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState("");
  const { setStep } = useFilterStepStore();
  const theme = useTheme();
  const lang = window.localStorage.getItem("language");
  const [selectedFilter, setSelectedFilter] = useState("ABC 순");
  const [selectedFilterDetail, setSelectedFilterDetail] = useState("ALL");
  const { getBrandSortList } = useApiRequest();

  const [tempBrandId, setTempBrandId] = useState<string | null>(
    searchParams.get("brand_id"),
  );

  const [tempBrandName, setTempBrandName] = useState<string | null>(
    searchParams.get("brand_name"),
  );

  const { data: brandSortList, isLoading: brandSortLoading } = useQuery({
    queryKey: ["brandSortList"],
    queryFn: getBrandSortList,
  });

  const getBrandList = useCallback(
    async ({ pageParam = 1 }) => {
      let url =
        selectedFilterDetail === "ALL"
          ? `${BRAND_URL}?page=${pageParam}&limit=${LOAD_SIZE_10}&order_by=${selectedFilter === "가나다 순" ? "KOREAN_ASC" : selectedFilter === "ABC 순" ? "ALPHABET_ASC" : "DATE_DESC"}&search_value=${inputValue}`
          : `${BRAND_URL}?page=${pageParam}&limit=${LOAD_SIZE_10}&order_by=${selectedFilter === "가나다 순" ? "KOREAN_ASC" : selectedFilter === "ABC 순" ? "ALPHABET_ASC" : "DATE_DESC"}&start_with=${selectedFilterDetail}&search_value=${inputValue}`;
      const res = await axiosInstance.get(url);
      if (res.status === 200) {
        return res.data;
      }
    },
    [axiosInstance, selectedFilter, inputValue, selectedFilterDetail],
  );

  const {
    data: brandList,
    fetchNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ["brandList", selectedFilter, inputValue, selectedFilterDetail],
    queryFn: ({ pageParam = 1 }) => getBrandList({ pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPages, pages) =>
      pages?.length < lastPages.data.total_count / LOAD_SIZE_10
        ? pages.length + 1
        : undefined,
    // enabled: !!inputValue,
  });

  const { setObserverTarget } = useIntersection(() => {
    fetchNextPage();
  });

  const handleBrandSelect = (brandId: string, brandName: string) => {
    if (tempBrandId === brandId) {
      setTempBrandId(null);
      setTempBrandName(null);
    } else {
      setTempBrandId(brandId);
      setTempBrandName(brandName);
    }
  };

  const handleApply = () => {
    const newParams = new URLSearchParams(searchParams);
    if (tempBrandId && tempBrandName) {
      newParams.set("brand_id", tempBrandId);
      newParams.set("brand_name", tempBrandName);
    } else {
      newParams.delete("brand_id");
      newParams.delete("brand_name");
    }
    setSearchParams(newParams);
    setStep(0);
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      return;
    }
  };

  return brandSortLoading ? null : (
    <Container>
      <InputWrapper>
        <BackButton onClick={() => setStep(1)}>
          <Image src={theme.image.arrow_left.default} />
        </BackButton>
        <SearchInput
          placeholder="브랜드 검색"
          inputValue={inputValue}
          setInputValue={setInputValue}
          onClick={handleKeyUp}
        />
      </InputWrapper>

      <BrandFilter
        filterList={brandSortList}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        selectedFilterList={
          selectedFilter === brandSortList[0]?.name_ko ? enFilter : koreanFilter
        }
        selectedFilterDetail={selectedFilterDetail}
        setSelectedFilterDetail={setSelectedFilterDetail}
      />

      {brandList?.pages?.[0]?.data.total_count === 0 ? (
        <NoSearch />
      ) : (
        <>
          <BrandList>
            {brandList?.pages?.map((page) => {
              return page?.data?.list?.map((item: any) => (
                <FilterItem
                  key={item.brand_id}
                  focus={item.brand_id === Number(tempBrandId)}
                  onClick={() =>
                    handleBrandSelect(
                      item.brand_id,
                      lang === "en" ? item.brand_name_en : item.brand_name_ko,
                    )
                  }>
                  <BrandCard
                    id={item.brand_id}
                    name={item.brand_name_en}
                    koreanName={item.brand_name_ko}
                    src={item.logo_url}
                    imageList={item.image_list}
                    withImage
                    onClick={() => {}}
                    liked={false}
                    brandSearch
                  />
                  <FilterItem focus={item.brand_id === tempBrandId}>
                    {item.brand_id === Number(tempBrandId) ? (
                      <Image src={theme.image.check_green.default} />
                    ) : (
                      <Image src={theme.image.check_gray.default} />
                    )}
                  </FilterItem>
                </FilterItem>
              ));
            })}
          </BrandList>
          <ButtonWrapper>
            <BasicButton
              title="적용"
              width="100%"
              height="5.2rem"
              onClick={handleApply}
            />
          </ButtonWrapper>

          <div ref={setObserverTarget} />
        </>
      )}
    </Container>
  );
};

export default BrandCategoryFilter;
